/**
 * Back in stock — spec 046, ADR-003 D5.
 *
 * Enhances the real `{% form 'contact' %}` rendered by `blocks/back-in-stock.liquid`
 * into a variant-scoped Klaviyo subscription, and keeps the variant it posts in
 * sync with the one the shopper has selected.
 *
 * ── EVERY WAY THIS CAN FAIL, AND WHAT THE SHOPPER GETS ─────────────────────
 * There is no state in which the control is dead and none in which it lies.
 *
 *   this module never loads / JS off      the browser posts the contact form.
 *                                         Merchant emailed, Shopify confirms.
 *   no Klaviyo Site ID configured         we never preventDefault. Same as above.
 *   invalid email address                 our own translated message, inline,
 *                                         focus kept on the field. (The browser's
 *                                         own bubble is in the BROWSER's language,
 *                                         not the storefront's — which is why
 *                                         `novalidate` is set from here, and only
 *                                         when we are actually going to handle it.)
 *   transport failed once                 translated retry message, typed address
 *                                         kept. Offline, blocked third party, DNS.
 *   transport failed twice                the contact form is posted for real.
 *   Klaviyo answered with an error        the contact form is posted for real —
 *                                         a wrong Site ID, an unsynced catalog
 *                                         variant or a rate limit does not get
 *                                         better by retrying.
 *   same address, same variant, again     a distinct "already on the list"
 *                                         message (046 §9.6).
 *
 * ⚠️ The native fallback re-submits THIS form. It is safe to do so because the
 * submit button carries no `name` and no `value` and every value the merchant
 * needs is a real named input — `form.submit()` serialises the form's own fields
 * only and drops a submitter's name/value pair. `requestSubmit()` is preferred
 * anyway so the two paths are identical.
 *
 * ── WHAT WE SEND, AND WHAT WE DO NOT ───────────────────────────────────────
 * A plain cross-origin POST to Klaviyo's public client endpoint. Public Site ID
 * only, no cookie of ours, and no dependency on `klaviyo.js` — which the app
 * embed does put on every page, but whose back-in-stock surface is undocumented
 * and whose copy would live in Klaviyo's admin in one language (046 §3).
 *
 * The storefront locale rides along as a profile property so the client's flow
 * has something to branch on. 046 §9.4 records that "does the client endpoint
 * accept profile properties in the same call" is unverified against a live
 * account — if it turns out not to, this request 4xx's and every shopper lands
 * on the contact-form path above, which is safe but silent, so the HTTP status
 * is logged loudly for Phase 10.
 */

import { StandardEvents } from '@shopify/events';

/**
 * @param {Element} source
 * @param {Element} target
 * @param {string} selector
 */
const copyValue = (source, target, selector) => {
  const from = source.querySelector(selector);
  const to = target.querySelector(selector);
  if (from instanceof HTMLInputElement && to instanceof HTMLInputElement) to.value = from.value;
};

class BackInStock extends HTMLElement {
  /** @type {AbortController | null} The in-flight subscription, one per element. */
  #request = null;

  /** @type {AbortController} Everything this element listens to. */
  #listeners = new AbortController();

  #busy = false;

  /** True once we have decided to let the browser post the form itself. */
  #native = false;

  /** Consecutive transport failures for the current form. */
  #failures = 0;

  #preconnected = false;

  /**
   * Addresses this page view has already registered, as `variantId|email`.
   * In memory, never in storage: it records only what happened here, so it
   * cannot tell one shopper that another is already subscribed (046 §9.5), it
   * cannot go stale on a shared device, and it puts no address at rest.
   * @type {Set<string>}
   */
  #seen = new Set();

  connectedCallback() {
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.addEventListener('submit', this.#onSubmit, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('focusin', this.#onFocusIn, { signal, once: true });

    this.closest('.shopify-section, dialog')?.addEventListener(
      StandardEvents.productSelect,
      this.#onProductSelect,
      { signal }
    );

    // Progressive enhancement, and only where it is honest: without a Site ID
    // we never intercept, so the browser must keep its own validation.
    if (this.dataset.companyId) this.querySelector('form')?.setAttribute('novalidate', '');
  }

  disconnectedCallback() {
    this.#listeners.abort();
    this.#request?.abort();
  }

  /** @param {Event} event */
  #onSubmit = (event) => {
    if (this.#native) return;

    const form = event.target;
    const email = this.querySelector('[data-bis-email]');
    const { companyId, endpoint, variantId } = this.dataset;

    // Nothing we can do better than the browser: leave the event alone and let
    // it post the contact form.
    if (!companyId || !endpoint || !variantId) return;
    if (!(form instanceof HTMLFormElement) || !(email instanceof HTMLInputElement)) return;

    event.preventDefault();
    if (this.#busy) return;

    if (!email.checkValidity()) {
      email.setAttribute('aria-invalid', 'true');
      this.#message('error', this.dataset.invalidEmail);
      email.focus();
      return;
    }

    email.removeAttribute('aria-invalid');
    this.#subscribe(form, email.value.trim());
  };

  /** Clears the invalid state on the next keystroke, per 046 §5. */
  #onInput = () => {
    const email = this.querySelector('[data-bis-email]');
    if (!(email instanceof HTMLInputElement) || !email.hasAttribute('aria-invalid')) return;
    email.removeAttribute('aria-invalid');
    this.#message(null, '');
  };

  /**
   * 046 §4: the connection is opened when the shopper touches the field, not in
   * <head>. Most sessions never make this request, and a preconnect in <head>
   * would spend a connection on all of them.
   */
  #onFocusIn = () => {
    if (this.#preconnected || !this.dataset.endpoint) return;
    this.#preconnected = true;
    try {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = new URL(this.dataset.endpoint).origin;
      link.crossOrigin = '';
      document.head.append(link);
    } catch {
      // A malformed endpoint is not worth breaking the form over.
    }
  };

  /**
   * @param {HTMLFormElement} form
   * @param {string} address
   */
  async #subscribe(form, address) {
    const key = `${this.dataset.variantId}|${address.toLowerCase()}`;
    if (this.#seen.has(key)) {
      this.#finish(this.dataset.already);
      return;
    }

    this.#setBusy(true);
    this.#request?.abort();
    this.#request = new AbortController();

    /** @type {Response} */
    let response;

    try {
      response = await fetch(
        `${this.dataset.endpoint}?company_id=${encodeURIComponent(this.dataset.companyId ?? '')}`,
        {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            accept: 'application/vnd.api+json',
            revision: this.dataset.revision ?? '',
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: address,
                      properties: { storefront_locale: this.dataset.locale ?? '' },
                    },
                  },
                },
              },
              relationships: {
                variant: {
                  // The Shopify catalog id shape. Variant-scoped by construction:
                  // there is no product-level subscription to fall back to.
                  data: { type: 'catalog-variant', id: `$shopify:::$default:::${this.dataset.variantId}` },
                },
              },
            },
          }),
          signal: this.#request.signal,
        }
      );
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      this.#setBusy(false);
      this.#failures += 1;
      if (this.#failures < 2) {
        this.#message('error', this.dataset.error);
        return;
      }
      console.warn('[back-in-stock] transport failed twice — posting the contact form instead', error);
      this.#submitNatively(form);
      return;
    }

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      console.warn(
        `[back-in-stock] Klaviyo refused the subscription (HTTP ${response.status}) — posting the contact form instead`,
        body.slice(0, 500)
      );
      this.#setBusy(false);
      this.#submitNatively(form);
      return;
    }

    this.#seen.add(key);
    this.#failures = 0;
    this.#setBusy(false);
    this.#finish(this.dataset.success);
  }

  /** @param {HTMLFormElement} form */
  #submitNatively(form) {
    this.#native = true;
    this.#setBusy(false);
    const button = this.querySelector('[data-bis-submit]');
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(button instanceof HTMLButtonElement ? button : undefined);
    } else {
      form.submit();
    }
  }

  /**
   * Terminal state: the form collapses to the message in place and focus moves
   * to it. Focus is never thrown to the top of the page and never taken from
   * the swatch the shopper just activated (046 §5).
   * @param {string} [text]
   */
  #finish(text) {
    this.#message('success', text);
    this.setAttribute('data-bis-done', '');
    const message = this.querySelector('[data-bis-message]');
    if (message instanceof HTMLElement) message.focus();
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.querySelector('[data-bis-actions]')?.setAttribute('aria-busy', String(busy));
    this.querySelector('[data-bis-submit]')?.setAttribute('aria-disabled', String(busy));
    if (busy) this.#message('busy', this.dataset.sending);
  }

  /**
   * @param {string | null} state
   * @param {string} [text]
   */
  #message(state, text) {
    const message = this.querySelector('[data-bis-message]');
    const target = this.querySelector('[data-bis-text]');
    if (!message || !target) return;
    target.textContent = text ?? '';
    if (state) message.setAttribute('data-bis-state', state);
    else message.removeAttribute('data-bis-state');
  }

  /** @param {Event} event */
  #onProductSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('product-card')) return;

    /** @type {{ promise?: Promise<{ detail?: Record<string, any> }> }} */
    const productSelect = /** @type {any} */ (event);

    productSelect.promise
      ?.then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.productId && !detail.newProduct && detail.productId !== this.dataset.productId) return;
        this.#sync(detail.html.querySelector('rp-back-in-stock'));
      })
      .catch((/** @type {unknown} */ error) => {
        if (error instanceof Error && error.name === 'AbortError') return;
        console.warn('[back-in-stock] variant change failed', error);
      });
  };

  /**
   * The load-bearing part: the variant id this form posts is the variant the
   * shopper has selected, because it is read out of the freshly rendered
   * section rather than remembered.
   * @param {Element | null} next
   */
  #sync(next) {
    this.#request?.abort();
    this.#setBusy(false);
    this.#native = false;
    this.#failures = 0;
    this.removeAttribute('data-bis-done');
    this.#message(null, '');
    this.querySelector('[data-bis-email]')?.removeAttribute('aria-invalid');

    if (!next || !next.hasAttribute('data-bis-active')) {
      this.removeAttribute('data-bis-active');
      this.hidden = true;
      return;
    }

    this.dataset.variantId = next.getAttribute('data-variant-id') ?? '';
    const productId = next.getAttribute('data-product-id');
    if (productId) this.dataset.productId = productId;

    copyValue(next, this, '[data-bis-variant-label]');
    copyValue(next, this, '[data-bis-variant-id]');
    copyValue(next, this, '[data-bis-body]');

    const intro = this.querySelector('[data-bis-intro]');
    const nextIntro = next.querySelector('[data-bis-intro]');
    if (intro && nextIntro) intro.textContent = nextIntro.textContent;

    // The typed address is deliberately left alone — a size change should not
    // make the shopper retype it.
    this.hidden = false;
    this.setAttribute('data-bis-active', '');
  }
}

if (!customElements.get('rp-back-in-stock')) {
  customElements.define('rp-back-in-stock', BackInStock);
}
